import type {
  PlaceOrderResponseDto,
  OrderDetailResponseDto,
  OrderDetailApiData,
  OrderHistoryRow,
  OrderHistoryResponseDto,
  OrderHistoryFiltersResponseDto,
  OrderHistoryFilters,
  PaginationDto,
  CalculateFeesResponseDto,
} from './orderContracts'
import type { Order, PlacedOrder, CalculateFees } from '../types/orderTypes'

const PERCENT_STRING_RE = /[%\s]/g
const parsePct = (raw: string | null | undefined): number =>
  Number((raw ?? '0').replace(PERCENT_STRING_RE, ''))

export const parseCalculateFees = (resp: CalculateFeesResponseDto): CalculateFees => {
  const d = resp.data ?? {}
  const fees = d.feesBreakdown ?? {}
  return {
    offeringId: d.offeringId ?? '',
    tokenPrice: Number(d.tokenPrice ?? 0),
    tokensAvailable: Number(d.tokensAvailable ?? 0),
    tokenQuantity: Number(d.tokenQuantity ?? 0),
    grossAmount: Number(d.grossAmount ?? 0),
    platformFeeAmount: Number(d.platformFeeAmount ?? 0),
    dldFeeAmount: Number(d.dldFeeAmount ?? 0),
    acquisitionFeeAmount: Number(d.acquisitionFeeAmount ?? 0),
    tokenisationFeeAmount: Number(d.tokenisationFeeAmount ?? 0),
    netAmount: Number(d.netAmount ?? 0),
    minTokens: Number(d.minTokens ?? 1),
    maxTokens: Number(d.maxTokens ?? 0),
    feesBreakdown: {
      dldFee: parsePct(fees.dldFee),
      acquisitionFee: parsePct(fees.acquisitionFee),
      tokenisationFee: parsePct(fees.tokenisationFee),
      platformFee: parsePct(fees.platformFee),
    },
  }
}

export const parsePlaceOrderResponse = (resp: PlaceOrderResponseDto): PlacedOrder => {
  const data = resp.data
  return {
    id: data?.id ?? '',
    orderReference: data?.orderReference ?? '',
    orderStatus: data?.orderStatus ?? '',
    propertyName: data?.propertyName ?? '',
    tokenQuantity: Number(data?.tokenQuantity ?? 0),
    tokenPrice: data?.tokenPrice ?? '0',
    grossAmount: data?.grossAmount ?? '0',
    platformFeeAmount: data?.platformFeeAmount ?? '0',
    dldFeeAmount: data?.dldFeeAmount ?? '0',
    acquisitionFeeAmount: data?.acquisitionFeeAmount ?? '0',
    tokenisationFeeAmount: data?.tokenisationFeeAmount ?? '0',
    netAmount: data?.netAmount ?? '0',
    currencyCode: data?.currencyCode ?? 'AED',
    walletAmount: data?.walletAmount ?? '0',
    pgAmount: data?.pgAmount ?? '0',
    paymentMethod: data?.paymentMethod ?? '',
    paymentConfirmedAt: data?.paymentConfirmedAt ?? '',
    createdAt: data?.createdAt ?? '',
    statusTracker: data?.statusTracker ?? [],
  }
}

export const parseOrderDetailResponse = (resp: OrderDetailResponseDto): OrderDetailApiData => {
  const data = resp.data ?? ({} as Partial<OrderDetailApiData>)
  return {
    id: data.id ?? '',
    orderReference: data.orderReference ?? '',
    orderStatus: data.orderStatus ?? '',
    offeringTitle: data.offeringTitle ?? '',
    propertyLocation: data.propertyLocation ?? '',
    propertyImage: data.propertyImage ?? null,
    statusTracker: data.statusTracker ?? [],
    fundingProgress: {
      fundedPercentage: Number(data.fundingProgress?.fundedPercentage ?? 0),
      remainingAmount: data.fundingProgress?.remainingAmount ?? '0',
      currencyCode: data.fundingProgress?.currencyCode ?? 'AED',
      closingInDays: Number(data.fundingProgress?.closingInDays ?? 0),
    },
    listingId: data.listingId ?? null,
    createdAt: data.createdAt ?? '',
    updatedAt: data.updatedAt ?? '',
    modificationsUsed: Number(data.modificationsUsed ?? 0),
    modificationsAllowed: Number(data.modificationsAllowed ?? 0),
    tokenQuantity: Number(data.tokenQuantity ?? 0),
    tokenPrice: data.tokenPrice ?? '0.00',
    investmentValue: data.investmentValue ?? '0.00',
    totalApplicableFee: data.totalApplicableFee ?? '0',
    feesBreakdown: {
      dldFee: data.feesBreakdown?.dldFee ?? null,
      dldFeeAmount: data.feesBreakdown?.dldFeeAmount ?? null,
      acquisitionFee: data.feesBreakdown?.acquisitionFee ?? null,
      acquisitionFeeAmount: data.feesBreakdown?.acquisitionFeeAmount ?? null,
      tokenisationFee: data.feesBreakdown?.tokenisationFee ?? null,
      tokenisationFeeAmount: data.feesBreakdown?.tokenisationFeeAmount ?? null,
      platformFeePercentage: data.feesBreakdown?.platformFeePercentage ?? null,
      platformFeeAmount: data.feesBreakdown?.platformFeeAmount ?? null,
    },
    totalPaid: data.totalPaid ?? '0.00',
    currencyCode: data.currencyCode ?? 'AED',
    paymentMethod: data.paymentMethod ?? '',
    transactionReference: data.transactionReference ?? null,
    canCancel: Boolean(data.canCancel ?? false),
  }
}

export const parseOrderHistoryRow = (row: OrderHistoryRow): Order => ({
  id: row.id,
  propertyTitle: row.propertyName,
  imageUrl: row.propertyImage ?? undefined,
  trackingId: row.orderReference,
  date: row.createdAt,
  tokens: row.tokenQuantity,
  pricePerToken: Number(row.tokenPrice),
  amount: Number(row.netAmount),
  status: row.orderStatus,
})

export interface ParsedOrderHistory {
  rows: Order[]
  pagination: PaginationDto
}

export const parseOrderHistory = (resp: OrderHistoryResponseDto): ParsedOrderHistory => ({
  rows: (resp.data ?? []).map(parseOrderHistoryRow),
  pagination: resp.pagination ?? { page: 1, limit: 10, totalItems: 0, totalPages: 1 },
})

export const parseOrderHistoryFilters = (
  resp: OrderHistoryFiltersResponseDto,
): OrderHistoryFilters => ({
  status: resp.data?.status ?? [],
  dateRange: resp.data?.dateRange ?? [],
  investmentAmount: resp.data?.investmentAmount ?? [],
  sort: resp.data?.sort ?? [],
})
